export type InlineNode = { type: 'text' | 'bold' | 'italic' | 'strike' | 'code' | 'link' | 'tag'; text: string; url?: string; children?: InlineNode[] };
export type TextBlock = { type: 'paragraph' | 'quote' | 'heading' | 'list'; lines: string[]; ordered?: boolean };

export function safeWebUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > 500 || /[\u0000-\u0020\\]/.test(value)) return null;
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password ? url.href : null; }
  catch { return null; }
}

export function inlinePostText(text: string, depth = 0): InlineNode[] {
  if (depth >= 3) return [{ type: 'text', text }];
  const pattern = /(`[^`\n]+`|\*\*[^*\n]+\*\*|~~[^~\n]+~~|\*[^*\n]+\*|_[^_\n]+_|\[[^\]\n]{1,100}\]\([^\s)]{1,500}\)|#[\p{L}\p{N}_]{2,32})/gu;
  const result: InlineNode[] = [];
  let position = 0;
  for (const match of text.matchAll(pattern)) {
    const start = match.index!; if (start > position) result.push({ type: 'text', text: text.slice(position, start) });
    const token = match[0];
    if (token.startsWith('`')) result.push({ type: 'code', text: token.slice(1, -1) });
    else if (token.startsWith('**')) result.push({ type: 'bold', text: token.slice(2, -2), children: inlinePostText(token.slice(2, -2), depth + 1) });
    else if (token.startsWith('~~')) result.push({ type: 'strike', text: token.slice(2, -2), children: inlinePostText(token.slice(2, -2), depth + 1) });
    else if (token.startsWith('*') || token.startsWith('_')) result.push({ type: 'italic', text: token.slice(1, -1), children: inlinePostText(token.slice(1, -1), depth + 1) });
    else if (token.startsWith('[')) {
      const parts = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token)!; const url = safeWebUrl(parts[2]);
      result.push(url ? { type: 'link', text: parts[1], url } : { type: 'text', text: parts[1] });
    } else result.push({ type: 'tag', text: token, url: `/explore?tag=${encodeURIComponent(token.slice(1))}` });
    position = start + token.length;
  }
  if (position < text.length) result.push({ type: 'text', text: text.slice(position) });
  return result;
}

export function postTextBlocks(body: string): TextBlock[] {
  const blocks: TextBlock[] = [];
  for (const line of body.replace(/\r/g, '').split('\n')) {
    if (!line.trim()) { if (blocks.at(-1)?.lines.at(-1) !== '') blocks.push({ type: 'paragraph', lines: [''] }); continue; }
    const heading = /^#{1,3}\s+(.+)$/.exec(line), quote = /^>\s?(.*)$/.exec(line), bullet = /^(?:[-*]\s+|\d+\.\s+)(.+)$/.exec(line);
    const type = heading ? 'heading' : quote ? 'quote' : bullet ? 'list' : 'paragraph';
    const ordered = /^\d+\.\s/.test(line), text = (heading || quote || bullet)?.[1] ?? line;
    const previous = blocks.at(-1);
    if (previous?.type === type && type !== 'heading' && (type !== 'list' || previous.ordered === ordered) && previous.lines.at(-1) !== '') previous.lines.push(text);
    else blocks.push({ type, lines: [text], ...(type === 'list' ? { ordered } : {}) });
  }
  return blocks;
}

export function plainPostText(body: string): string {
  const plainInline = (nodes: InlineNode[]): string => nodes.map(node => node.children ? plainInline(node.children) : node.text).join('');
  return postTextBlocks(body).map(block => block.lines.map(line => plainInline(inlinePostText(line))).join('\n')).join('\n').trim();
}

export type FormatAction = 'bold' | 'italic' | 'strike' | 'code' | 'quote' | 'list' | 'heading';
export function formatSelection(value: string, start: number, end: number, action: FormatAction): { value: string; start: number; end: number } {
  const wrappers = { bold: '**', italic: '*', strike: '~~', code: '`' };
  if (action in wrappers) {
    const marker = wrappers[action as keyof typeof wrappers], selected = value.slice(start, end) || 'text';
    return { value: value.slice(0, start) + marker + selected + marker + value.slice(end), start: start + marker.length, end: start + marker.length + selected.length };
  }
  const lineStart = value.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
  const after = value.indexOf('\n', end), lineEnd = after === -1 ? value.length : after;
  const prefix = action === 'quote' ? '> ' : action === 'list' ? '- ' : '## ';
  const selected = value.slice(lineStart, lineEnd).split('\n').map(line => prefix + line).join('\n');
  return { value: value.slice(0, lineStart) + selected + value.slice(lineEnd), start: lineStart, end: lineStart + selected.length };
}
